/* MSAL PKCE configuration for the Daedalus SPA.

   The SPA acquires a delegated access token on behalf of the signed-in
   user. APIM's existing JWT validator reads `oid` from that token and
   injects it as X-User-OID — same path Tools Service already consumes.
   Preflight-auth is called WITHOUT on_behalf_of; the else branch in
   agent_queries.py sets effective_user_oid = caller_oid. See
   CLAUDE.md invariant 8 and the plan's "Identity model" section.

   Environment variables (from .env.local):
     VITE_MSAL_CLIENT_ID  — the syncon-daedalus app registration's client ID
     VITE_MSAL_TENANT_ID  — Syncon tenant (GUID)
     VITE_API_SCOPE       — the existing APIM resource app ID URI + /.default,
                            read verbatim from the current APIM JWT
                            validation policy. No new audience is minted.
     VITE_APIM_BASE_URL   — APIM base URL (e.g. https://apim-syncon.azure-api.net) */

import type { Configuration, RedirectRequest } from '@azure/msal-browser';

const CLIENT_ID = import.meta.env.VITE_MSAL_CLIENT_ID;
const TENANT_ID = import.meta.env.VITE_MSAL_TENANT_ID;
const API_SCOPE = import.meta.env.VITE_API_SCOPE;

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin + '/sign-in',
    // false: we don't want MSAL to restore the original URL after processing
    // the redirect. React Router owns the URL; MSAL just needs to consume
    // the `#code=...` fragment and hand us an account. If this were true,
    // MSAL would issue its own navigation after processing, which
    // double-navigates against React Router and can strand the app.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage', // tokens scoped to the tab
    storeAuthStateInCookie: false,
  },
  system: {
    // Let MSAL retry transient network failures rather than bubbling
    // them into the sign-in UX.
    allowRedirectInIframe: false,
  },
};

/** Interactive login scope — passed to loginRedirect / loginPopup.
 *
 *  `prompt: 'select_account'` forces Entra's account picker on every
 *  EXPLICIT sign-in click. Without it, a leftover Entra SSO cookie (e.g.
 *  a previous user on a shared trailer PC who abandoned the sign-out
 *  page) is adopted silently and the new user lands in the wrong
 *  session with no chance to intervene. Seamless sign-in for the
 *  same-browser multi-tab case is handled separately by ssoSilent at
 *  boot (MsalBootstrap), which deliberately does NOT use this prompt. */
export const loginRequest: RedirectRequest = {
  scopes: [API_SCOPE || 'openid', 'profile', 'openid'],
  prompt: 'select_account',
};

/** Silent scope — passed to acquireTokenSilent before every API call. */
export const apiTokenRequest: { scopes: string[] } = {
  scopes: [API_SCOPE],
};

/** Graph scope used to mint an Office for the Web embed URL in the user's
 *  name (vs. the Graph SP's), so SharePoint decides whether they may open
 *  the file and the iframe is edit-capable. The server opens a file under
 *  its own identity only for the user's own run or a document the platform
 *  produced that they may read (read-only); anything else needs this.
 *
 *  Acquired by the artifact panel's "Sign in and open" button (silent,
 *  then a popup) when the silent graphSilentScopes token was unavailable. */
export const graphFilesScope: { scopes: string[] } = {
  scopes: ['https://graph.microsoft.com/Files.ReadWrite.All'],
};

/** Azure Storage, delegated — the 401(k) workbooks are downloaded straight
 *  from the payroll storage account in the USER's name, so Azure itself
 *  admits only the "Daedalus 401k Files" Entra group and no platform service
 *  ever holds the bytes (docs/PAYROLL_401K_REPORTS.md). Admin-consented on the
 *  syncon-Daedalus-dev registration. A user's token reaches only storage
 *  their own Azure roles allow — for everyone else, nothing. */
export const storageScope: { scopes: string[] } = {
  scopes: ['https://storage.azure.com/user_impersonation'],
};

/** Graph scopes acquired SILENTLY (no popup). All are admin-consented
 *  tenant-wide on the syncon-Daedalus-dev registration, so acquireTokenSilent
 *  succeeds without any prompt. A single token carries all of them and is sent
 *  as X-Graph-Token: on preflight it's bound to the Foundry session, and the
 *  proxy forwards it ONLY to the per-user Graph read tools (calendar/email)
 *  and the file opener (open_office_file).
 *    - Sites.Read.All   — preflight probes, AS the user, which restricted
 *      SharePoint sites they can open (X-Graph-Token -> accessible_libraries).
 *    - Files.ReadWrite.All — mints the Office embed in the user's name so
 *      SharePoint enforces at view time (delegated preview), and lets the
 *      open_office_file agent tool find and open files as the user.
 *    - Calendars.Read   — the list_my_calendar / get_my_free_busy agent tools.
 *    - Mail.Read        — the search_my_email / read_my_email agent tools.
 *    - Mail.Send        — the SPA-direct, user-confirmed email send (the agent
 *      only drafts; it can never send). Token scope breadth does NOT widen the
 *      agent's reach — the capability boundary is the endpoint surface (there is
 *      no send agent tool), not the token's scopes.
 *  On silent failure the getter returns null and callers degrade safely
 *  (preflight: probe no-ops; embed: the server opens only the user's own run or
 *  a document the platform produced that they may read, and anything else
 *  answers delegated_token_required, which the artifact panel turns into a
 *  sign-in button; Graph tools and the file opener: the backend returns
 *  delegated_token_required). */
export const graphSilentScopes: { scopes: string[] } = {
  scopes: [
    'https://graph.microsoft.com/Sites.Read.All',
    'https://graph.microsoft.com/Files.ReadWrite.All',
    'https://graph.microsoft.com/Calendars.Read',
    'https://graph.microsoft.com/Mail.Read',
    'https://graph.microsoft.com/Mail.Send',
  ],
};

/** Graph scope for reading the signed-in user's own directory profile
 *  (jobTitle, department) via /me. Kept SEPARATE from graphSilentScopes on
 *  purpose: User.Read is a default delegated permission with a different
 *  consent lineage than the admin-consented Sites/Files scopes, so bundling
 *  them would couple their silent-acquisition failure modes — a consent gap
 *  on one would blank the other (e.g. break SharePoint trimming). Resolving
 *  the org title is best-effort; on any failure the caller hides the title. */
export const graphUserScope: { scopes: string[] } = {
  scopes: ['https://graph.microsoft.com/User.Read'],
};

/** True when VITE_DEV_BYPASS_MSAL=true — local-dev-loop mode where the SPA
 *  skips MSAL redirect, injects a fake user, but still calls real local
 *  backends (localhost:8001, etc). Different from VITE_USE_MOCKS (which
 *  is fully offline and MSW-intercepted). */
export const DEV_BYPASS_MSAL: boolean =
  import.meta.env.VITE_DEV_BYPASS_MSAL === 'true';

/** True when MSAL environment is fully configured AND we're not in
 *  walkthrough / dev-bypass modes. False means use MockBootstrap. */
export const MSAL_ENABLED: boolean = Boolean(
  CLIENT_ID &&
    TENANT_ID &&
    API_SCOPE &&
    import.meta.env.VITE_USE_MOCKS !== 'true' &&
    !DEV_BYPASS_MSAL
);
